import { Server } from 'lucide-react';
import { useMemo } from 'react';

import { IndexOptional } from '@/react/kubernetes/configs/types';
import { createStore } from '@/react/kubernetes/datatables/default-kube-datatable-store';
import { useEnvironment } from '@/react/portainer/environments/queries';
import { useEnvironmentId } from '@/react/hooks/useEnvironmentId';

import { Datatable } from '@@/datatables';
import { useTableState } from '@@/datatables/useTableState';

import { ApplicationPod } from '../ListView/ApplicationsDatatable/types';

import { ContainerRowData } from './ApplicationContainersDatatable/types';
import { getColumns } from './ApplicationContainersDatatable/columns';

const storageKey = 'k8sCustomResourcePodsDatatable';
const settingsStore = createStore(storageKey);

export function CustomResourcePodsDatatable({
  pods,
}: {
  pods: ApplicationPod[];
}) {
  const environmentId = useEnvironmentId();
  const useServerMetricsQuery = useEnvironment(
    environmentId,
    (env) => !!env?.Kubernetes?.Configuration.UseServerMetrics
  );
  const tableState = useTableState(settingsStore, storageKey);

  const containerRowData = useCustomResourcePodRowData(pods);

  return (
    <Datatable<IndexOptional<ContainerRowData>>
      dataset={containerRowData}
      columns={getColumns(!!useServerMetricsQuery.data)}
      settingsManager={tableState}
      isLoading={useServerMetricsQuery.isLoading}
      title="Custom Resource pods"
      titleIcon={Server}
      getRowId={(row) => row.podName}
      disableSelect
      data-cy="k8s-custom-resource-containers-datatable"
    />
  );
}

// Transform ApplicationPod[] into ContainerRowData[]
// TODO: This is showing information for each pod, instead of all containers in each pod. This could have negative effects, like log and console errors from an incorrect container name.
function useCustomResourcePodRowData(
  pods: ApplicationPod[]
): ContainerRowData[] {
  return useMemo(
    () =>
      pods.map((pod) => ({
        name: pod.ContainerName,
        image: pod.Image,
        imagePullPolicy: pod.ImagePullPolicy,
        podName: pod.Name,
        nodeName: pod.NodeName,
        podIp: pod.PodIP,
        creationDate: pod.CreationDate,
        status: getStatusType(pod.Status),
      })),
    [pods]
  );
}

// https://kubernetes.io/docs/concepts/workloads/pods/pod-lifecycle/#pod-phase
// TODO: This is showing information for each pod, instead of all containers in each pod. This could have negative effects, like log and console errors from an incorrect container name.
function getStatusType(status: string): ContainerRowData['status'] {
  switch (status) {
    case 'Pending':
      return {
        status: 'Pending',
        type: 'muted',
      };
    case 'Running':
      return {
        status: 'Running',
        type: 'success',
      };
    case 'Succeeded':
      return {
        status: 'Succeeded',
        type: 'success',
      };
    case 'Failed':
      return {
        status: 'Failed',
        type: 'danger',
      };
    case 'Unknown':
      return {
        status: 'Unknown',
        type: 'muted',
      };
    default:
      return {
        status: 'Unknown',
        type: 'muted',
      };
  }
}
